import { useEffect, useState } from "react";
import {
  CheckCircle,
  Users,
  Globe,
  Leaf,
  HeartHandshake,
  SunMoon,
  HandMetal,
  ShieldCheck,
  ChevronRight,
  Quote,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import HomeHeader2 from "@/components/homePage/HomeHeader2";
import panorama from "../Images/panorama2.jpg";
import producteurs from "../Images/producteurs.jpeg";
import communaute from "../Images/communaute.jpeg";
import developpement from "../Images/developpement.jpg";
import qualite from "../Images/qualite.png";
import equipe from "../Images/equipe.png";
// Couleurs du drapeau du Niger (reprises du code existant)
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

// Images pour les différentes sections (à remplacer par vos propres images)
const IMAGES = {
  hero: panorama, // Image représentant l'engagement
  farmers: producteurs, // Image de producteurs locaux
  community: communaute, // Image de communauté locale
  sustainability: developpement, // Image liée au développement durable
  quality: qualite, // Image représentant la qualité
  team: equipe, // Image de l'équipe
  ousseina: equipe,
  abdoulRazak: equipe,
};

export default function NotreEngagementPage() {
  const [activeAccordion, setActiveAccordion] = useState("fairTrade");

  const toggleAccordion = (key) => {
    setActiveAccordion(activeAccordion === key ? null : key);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Contenu des accordéons pour la section principes
  const principlesContent = {
    fairTrade: {
      title: "Commerce équitable",
      icon: (
        <HeartHandshake className="w-6 h-6" style={{ color: COLORS.orange }} />
      ),
      content: (
        <div>
          <p className="mb-4">
            Nous adhérons aux principes du commerce équitable en garantissant
            une rémunération juste à tous les acteurs de notre chaîne
            d'approvisionnement. Les prix que nous payons à nos producteurs sont
            supérieurs aux prix du marché conventionnel.
          </p>
          <p>
            Nos partenaires bénéficient de relations commerciales durables et
            transparentes, leur permettant d'investir dans leur avenir et celui
            de leur communauté. Nous organisons régulièrement des visites chez
            nos producteurs pour maintenir ces relations de proximité.
          </p>
        </div>
      ),
    },
    socialImpact: {
      title: "Impact social",
      icon: <Users className="w-6 h-6" style={{ color: COLORS.orange }} />,
      content: (
        <div>
          <p className="mb-4">
            Nous investissons une partie de nos bénéfices dans des projets
            communautaires au Niger : programmes d'alphabétisation, accès à
            l'eau potable, et formation professionnelle des artisans.
          </p>
          <p>
            Chaque achat que vous effectuez contribue directement à ces
            initiatives. Nous publions des rapports annuels détaillant l'impact
            de ces projets et garantissons une transparence totale sur
            l'utilisation des fonds alloués.
          </p>
        </div>
      ),
    },
    sustainability: {
      title: "Durabilité environnementale",
      icon: <Leaf className="w-6 h-6" style={{ color: COLORS.green }} />,
      content: (
        <div>
          <p className="mb-4">
            Notre engagement environnemental se manifeste à chaque étape de
            notre activité. Nous encourageons les pratiques agricoles durables
            auprès de nos producteurs et utilisons des emballages écologiques
            pour nos produits.
          </p>
          <p>
            Nous optimisons notre logistique pour réduire notre empreinte
            carbone et compensons les émissions résiduelles en participant à des
            projets de reforestation au Niger. Notre objectif est d'atteindre la
            neutralité carbone d'ici 2026.
          </p>
        </div>
      ),
    },
    quality: {
      title: "Qualité et authenticité",
      icon: <CheckCircle className="w-6 h-6" style={{ color: COLORS.green }} />,
      content: (
        <div>
          <p className="mb-4">
            Nous sélectionnons rigoureusement chaque produit pour garantir une
            qualité irréprochable. Nos épices, nos produits artisanaux et nos
            denrées alimentaires respectent des normes strictes et sont
            régulièrement contrôlés.
          </p>
          <p>
            L'authenticité est au cœur de notre démarche : nos produits sont
            fabriqués selon des méthodes traditionnelles, préservant ainsi le
            patrimoine culturel nigérien. Nous refusons toute standardisation
            excessive qui dénaturerait l'essence de ces créations.
          </p>
        </div>
      ),
    },
  };

  return (
    <>
      <HomeHeader2 />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="relative h-96">
          <div className="absolute inset-0">
            <img
              src={IMAGES.hero}
              alt="Engagement envers les communautés du Niger"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 to-transparent"></div>
          </div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="max-w-2xl text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Notre Engagement
              </h1>
              <p className="text-xl opacity-90 mb-6">
                Bâtir un commerce plus juste, respectueux des hommes et de la
                planète
              </p>
              <div className="flex space-x-3">
                <a
                  href="#principles"
                  className="px-6 py-3 rounded-full font-medium bg-white text-green-800 hover:bg-green-50 transition shadow-md"
                >
                  Découvrir nos principes
                </a>
                <a
                  href="/ContactPage"
                  className="px-6 py-3 rounded-full font-medium text-white border-2 border-white hover:bg-white/10 transition"
                >
                  Nous contacter
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <div
                className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-6"
                style={{
                  backgroundColor: COLORS.lightGreen,
                  color: COLORS.green,
                }}
              >
                Notre philosophie
              </div>
              <h2 className="text-3xl font-bold mb-6">
                Un commerce qui a du sens
              </h2>
              <p className="text-gray-600 mb-8">
                Chez Kassarmou, nous croyons qu'il est possible de conjuguer
                commerce et valeurs humaines. Notre entreprise est née de la
                volonté de créer des liens durables entre les producteurs
                nigériens et les consommateurs du monde entier, dans le respect
                des personnes et de l'environnement.
              </p>
              <div className="border-t border-gray-200 w-20 mx-auto mb-8"></div>
              <div className="text-left">
                <p className="text-gray-600 mb-4">
                  Notre modèle repose sur quatre piliers fondamentaux qui
                  guident chacune de nos actions :
                </p>
                <ul className="space-y-2 mb-8">
                  <li className="flex items-center">
                    <CheckCircle
                      className="w-5 h-5 mr-2"
                      style={{ color: COLORS.green }}
                    />
                    <span>
                      Rémunération équitable des producteurs et artisans
                    </span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle
                      className="w-5 h-5 mr-2"
                      style={{ color: COLORS.green }}
                    />
                    <span>Préservation des savoir-faire traditionnels</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle
                      className="w-5 h-5 mr-2"
                      style={{ color: COLORS.green }}
                    />
                    <span>
                      Respect de l'environnement et pratiques durables
                    </span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle
                      className="w-5 h-5 mr-2"
                      style={{ color: COLORS.green }}
                    />
                    <span>Transparence totale sur notre chaîne de valeur</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Nos principes en accordéon */}
        <section
          id="principles"
          className="py-16"
          style={{ backgroundColor: COLORS.paleGreen }}
        >
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <div
                className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4"
                style={{
                  backgroundColor: COLORS.lightOrange,
                  color: COLORS.orange,
                }}
              >
                Nos principes
              </div>
              <h2 className="text-3xl font-bold">Des engagements concrets</h2>
            </div>

            <div className="max-w-3xl mx-auto">
              {Object.entries(principlesContent).map(([key, principle]) => (
                <div key={key} className="mb-4">
                  <button
                    onClick={() => toggleAccordion(key)}
                    className={`w-full flex items-center justify-between p-4 rounded-lg shadow-md transition-colors ${
                      activeAccordion === key
                        ? "bg-white"
                        : "bg-white hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center">
                      <div className="mr-3">{principle.icon}</div>
                      <h3 className="text-xl font-medium">{principle.title}</h3>
                    </div>
                    <ChevronRight
                      className={`w-5 h-5 transition-transform ${
                        activeAccordion === key ? "transform rotate-90" : ""
                      }`}
                      style={{ color: COLORS.gray }}
                    />
                  </button>
                  {activeAccordion === key && (
                    <div className="mt-2 p-6 bg-white rounded-lg shadow-md">
                      {principle.content}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Nos partenariats */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <div
                className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4"
                style={{
                  backgroundColor: COLORS.lightGreen,
                  color: COLORS.green,
                }}
              >
                Partenariats
              </div>
              <h2 className="text-3xl font-bold">Nos partenaires locaux</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <img
                  src={IMAGES.farmers}
                  alt="Producteurs locaux au Niger"
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">
                    Coopératives agricoles
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Nous travaillons avec plusieurs coopératives agricoles dans
                    les régions de Maradi, Niamey et Zinder. Ces coopératives
                    regroupent plus de 400 producteurs qui cultivent et
                    transforment les épices, les arachides et d'autres produits
                    agricoles que nous commercialisons.
                  </p>
                  <p className="text-gray-600">
                    Notre collaboration va au-delà de simples relations
                    commerciales : nous investissons dans la formation aux
                    méthodes durables, fournissons des équipements modernes et
                    garantissons des contrats à long terme pour stabiliser leurs
                    revenus.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <img
                  src={IMAGES.community}
                  alt="Artisans du Niger"
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">
                    Collectifs d'artisans
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Nos articles d'artisanat sont créés par des collectifs
                    d'artisans de la région d'Agadez, célèbre pour son travail
                    du cuir et de l'argent, et de Maradi, connue pour ses
                    textiles traditionnels. Ces collectifs regroupent près de
                    150 artisans, principalement des femmes.
                  </p>
                  <p className="text-gray-600">
                    Nous leur offrons un accès au marché européen tout en
                    préservant l'authenticité de leurs créations et en
                    valorisant leurs techniques ancestrales.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Impact et certifications */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <div
                  className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-6"
                  style={{
                    backgroundColor: COLORS.lightOrange,
                    color: COLORS.orange,
                  }}
                >
                  Notre impact
                </div>
                <h2 className="text-3xl font-bold mb-6">
                  Des résultats concrets
                </h2>
                <p className="text-gray-600 mb-6">
                  Depuis la création de Kassarmou, notre impact s'est traduit
                  par des améliorations mesurables pour nos partenaires
                  nigériens :
                </p>

                <div className="space-y-4">
                  <div className="flex">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: COLORS.lightGreen }}
                    >
                      <Globe
                        className="w-6 h-6"
                        style={{ color: COLORS.green }}
                      />
                    </div>
                    <div className="ml-4">
                      <h3 className="font-bold mb-1">Revenus augmentés</h3>
                      <p className="text-gray-600">
                        Nos producteurs et artisans ont vu leurs revenus
                        augmenter de 45% en moyenne depuis le début de notre
                        partenariat.
                      </p>
                    </div>
                  </div>

                  <div className="flex">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: COLORS.lightGreen }}
                    >
                      <SunMoon
                        className="w-6 h-6"
                        style={{ color: COLORS.green }}
                      />
                    </div>
                    <div className="ml-4">
                      <h3 className="font-bold mb-1">
                        Émancipation des femmes
                      </h3>
                      <p className="text-gray-600">
                        70% de nos partenaires sont des femmes qui ont désormais
                        une source de revenus stable et une reconnaissance de
                        leur savoir-faire.
                      </p>
                    </div>
                  </div>

                  <div className="flex">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: COLORS.lightGreen }}
                    >
                      <HandMetal
                        className="w-6 h-6"
                        style={{ color: COLORS.green }}
                      />
                    </div>
                    <div className="ml-4">
                      <h3 className="font-bold mb-1">
                        Préservation culturelle
                      </h3>
                      <p className="text-gray-600">
                        15 techniques artisanales traditionnelles ont été
                        revitalisées grâce à la demande créée par notre
                        commerce.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* <div>
                <div className="bg-white p-8 rounded-xl shadow-lg">
                  <h3 className="text-xl font-bold mb-6">Nos certifications</h3>
                  <div className="space-y-6">
                    <div className="flex items-center">
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: COLORS.lightOrange }}
                      >
                        <ShieldCheck
                          className="w-8 h-8"
                          style={{ color: COLORS.orange }}
                        />
                      </div>
                      <div className="ml-4">
                        <h4 className="font-bold">Fair Trade Certified</h4>
                        <p className="text-gray-600 text-sm">
                          Certification garantissant des conditions commerciales
                          équitables
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: COLORS.lightOrange }}
                      >
                        <Leaf
                          className="w-8 h-8"
                          style={{ color: COLORS.orange }}
                        />
                      </div>
                      <div className="ml-4">
                        <h4 className="font-bold">Organic Certified</h4>
                        <p className="text-gray-600 text-sm">
                          Nos épices et produits alimentaires sont cultivés sans
                          pesticides
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: COLORS.lightOrange }}
                      >
                        <Globe
                          className="w-8 h-8"
                          style={{ color: COLORS.orange }}
                        />
                      </div>
                      <div className="ml-4">
                        <h4 className="font-bold">B Corp Certified</h4>
                        <p className="text-gray-600 text-sm">
                          Entreprise répondant aux normes sociales et
                          environnementales les plus strictes
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </section>

        {/* Notre équipe */}
        <section className="py-16 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto px-4 max-w-6xl">
            {/* En-tête de section */}
            <div className="text-center mb-12">
              <div
                className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4"
                style={{
                  backgroundColor: COLORS.lightGreen,
                  color: COLORS.green,
                }}
              >
                Qui sommes-nous
              </div>
              <h2 className="text-4xl font-bold tracking-tight">
                L'équipe Kassarmou
              </h2>
            </div>

            {/* Image principale */}
            {/* <div className="mb-16">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img
                  src={IMAGES.team}
                  alt="L'équipe Kassarmou"
                  className="w-full h-96 object-cover transform hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end">
                  <div className="p-8 text-white">
                    <h3 className="text-2xl font-bold mb-2">
                      Une vision commune
                    </h3>
                    <p className="text-white/80">
                      Faire rayonner le Niger à travers le monde
                    </p>
                  </div>
                </div>
              </div>
            </div> */}

            {/* Présentation générale */}
            <Card className="mb-16 border-none shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-start mb-6">
                  <Quote className="text-green-600 flex-shrink-0 mr-4 w-10 h-10" />
                  <p className="text-lg text-gray-700 italic">
                    Derrière Kassarmou, il y a une équipe passionnée, ancrée
                    dans ses racines et tournée vers l'avenir.
                  </p>
                </div>

                <div className="space-y-6 text-gray-600">
                  <p>
                    Entre savoir-faire, vision et amour du pays, chacun apporte
                    sa pierre à l'édifice pour faire rayonner le Niger au-delà
                    des frontières.
                  </p>
                  <p>
                    Nous partageons la conviction que le commerce peut être une
                    force de changement positif. Notre équipe travaille en
                    étroite collaboration avec nos partenaires sur le terrain,
                    effectuant des visites régulières au Niger pour maintenir
                    ces liens précieux et s'assurer que nos engagements sont
                    respectés.
                  </p>
                  {/* <p>
                    Chaque membre apporte son expertise spécifique – que ce soit
                    dans l'agriculture durable, le commerce équitable, le design
                    ou la logistique – pour faire de Kassarmou une entreprise à
                    impact qui relie véritablement l'Afrique et l'Europe.
                  </p> */}
                </div>
              </CardContent>
            </Card>

            {/* Membres de l'équipe */}
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {/* Ousseina */}
              <Card className="overflow-hidden transition-all duration-300 hover:shadow-2xl">
                <div className="flex flex-col md:flex-row">
                  {/* <div className="md:w-2/5">
                    <div className="h-full">
                      <img
                        src={IMAGES.ousseina}
                        alt="Ousseina - Fondatrice"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div> */}
                  <div className="md:w-3/5 p-6">
                    <div
                      className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4"
                      style={{
                        backgroundColor: COLORS.lightGreen,
                        color: COLORS.green,
                      }}
                    >
                      Fondatrice
                    </div>
                    <h3 className="text-2xl font-bold mb-2">
                      Laouali Yacouba Ousseina
                    </h3>
                    <p className="text-gray-600">
                      Visionnaire et attachée à ses origines, Ousseina a lancé
                      Kassarmou avec un objectif clair : permettre à chaque
                      Nigérien de la diaspora de garder un lien fort avec sa
                      culture. Elle supervise l'ensemble du projet avec passion
                      et exigence.
                    </p>
                  </div>
                </div>
              </Card>

              {/* Abdoul Razak */}
              <Card className="overflow-hidden transition-all duration-300 hover:shadow-2xl">
                <div className="flex flex-col md:flex-row">
                  {/* <div className="md:w-2/5">
                    <div className="h-full">
                      <img
                        src={IMAGES.abdoulRazak}
                        alt="Abdoul Razak - Développeur Web"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div> */}
                  <div className="md:w-4/5 p-6">
                    <div
                      className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4"
                      style={{
                        backgroundColor: COLORS.lightGreen,
                        color: COLORS.green,
                      }}
                    >
                      Développeur Web
                    </div>
                    <h3 className="text-2xl font-bold mb-2">
                      Adamou Abdoul Razak
                    </h3>
                    <p className="text-gray-600">
                      Architecte digital de Kassarmou, Abdoul Razak est le
                      talent technique derrière la boutique en ligne. Grâce à
                      son expertise, il a conçu un site fluide, moderne et
                      fiable, pour une expérience d'achat à la hauteur des
                      attentes de la diaspora.
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Communauté */}
            <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-none shadow-lg">
              <CardContent className="p-8">
                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-4">
                    Et vous, notre communauté
                  </h3>
                  <div className="flex justify-center mb-6">
                    <div className="flex -space-x-4">
                      {[1, 2, 3, 4, 5].map((_, index) => (
                        <Avatar
                          key={index}
                          className="border-2 border-white w-12 h-12"
                        >
                          <AvatarFallback className="bg-gray-200 text-gray-600">
                            {String.fromCharCode(65 + index)}
                          </AvatarFallback>
                        </Avatar>
                      ))}
                      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 border-2 border-white text-sm font-medium">
                        +99
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 max-w-2xl mx-auto">
                    Vous êtes au cœur du projet. Vos retours, vos partages et
                    vos commandes nous inspirent et nous portent. Merci de
                    porter avec nous cette aventure humaine, culturelle et
                    entrepreneuriale.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Call to Action */}
        <section
          className="py-16"
          style={{ backgroundColor: COLORS.lightOrange }}
        >
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold mb-6">
                Participez à notre aventure
              </h2>
              <p className="text-gray-700 mb-8">
                En choisissant nos produits, vous rejoignez une communauté
                engagée qui soutient un commerce plus juste et plus respectueux.
                Découvrez notre sélection et faites la différence à chaque
                achat.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="/produits"
                  className="px-8 py-3 rounded-full font-medium text-white shadow-md hover:shadow-lg transition-shadow"
                  style={{ backgroundColor: COLORS.orange }}
                >
                  Découvrir nos produits
                </a>
                <a
                  href="/ContactPage"
                  className="px-8 py-3 rounded-full font-medium shadow-md hover:shadow-lg transition-shadow"
                  style={{
                    backgroundColor: COLORS.green,
                    color: COLORS.white,
                  }}
                >
                  Nous contacter
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
